import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { confirmShipmentInternationalFees, createManualShipmentEvent, fetchShipmentById, markShipmentSent } from '../../services/shipment';

const getNowDateTimeLocal = () => {
  const now = new Date();
  const offset = now.getTimezoneOffset();
  const local = new Date(now.getTime() - (offset * 60000));
  return local.toISOString().slice(0, 16);
};

const toApiDateTime = (value) => {
  if (!value) return undefined;
  const dt = new Date(value);
  return Number.isNaN(dt.getTime()) ? undefined : dt.toISOString();
};

const toApiDateOnly = (value) => {
  if (!value) return undefined;
  const dt = new Date(`${value}T00:00:00`);
  return Number.isNaN(dt.getTime()) ? undefined : value;
};

const formatBracketDateTime = (value) => {
  if (!value) return 'To be updated';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return 'To be updated';
  const pad = (n) => String(n).padStart(2, '0');
  const year = dt.getFullYear();
  const month = pad(dt.getMonth() + 1);
  const day = pad(dt.getDate());
  const hour = pad(dt.getHours());
  const minute = pad(dt.getMinutes());
  const second = pad(dt.getSeconds());
  return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
};

const parseApiError = (err) => {
  const data = err?.response?.data;
  if (!data) return 'Request failed';
  if (typeof data.error === 'string') return data.error;
  if (typeof data.detail === 'string') return data.detail;
  if (typeof data === 'string') return data;
  if (typeof data === 'object') {
    const firstKey = Object.keys(data)[0];
    const value = data[firstKey];
    if (Array.isArray(value) && value.length) return String(value[0]);
    if (typeof value === 'string') return value;
  }
  return 'Request failed';
};

export default function ShipmentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [shipment, setShipment] = useState(null);
  const [submittingManualEvent, setSubmittingManualEvent] = useState(false);
  const [confirmingFees, setConfirmingFees] = useState(false);
  const [markingSent, setMarkingSent] = useState(false);
  const [manualForm, setManualForm] = useState({
    status: '',
    location: '',
    event_time: getNowDateTimeLocal(),
    note: '',
    update_eta: '',
    update_pickup_date: '',
    update_summary: '',
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [quickDateDialog, setQuickDateDialog] = useState({
    open: false,
    action: null,
    value: '',
  });

  const loadShipment = useCallback(async () => {
    const data = await fetchShipmentById(id);
    setShipment(data);
  }, [id]);

  useEffect(() => {
    loadShipment();
  }, [loadShipment]);

  const timeline = useMemo(() => {
    const events = [...(shipment?.events || [])];
    events.sort((a, b) => new Date(b.event_time) - new Date(a.event_time));
    return events;
  }, [shipment]);

  const totalVolumeWeight = useMemo(() => (
    (shipment?.items || []).reduce((sum, item) => {
      const value = Number(item.volume_weight);
      return sum + (Number.isFinite(value) ? value : 0);
    }, 0)
  ), [shipment]);

  const totalCbm = useMemo(() => (
    (shipment?.items || []).reduce((sum, item) => {
      const value = Number(item.package_cbm);
      return sum + (Number.isFinite(value) ? value : 0);
    }, 0)
  ), [shipment]);

  const canSubmitManualEvent = !!manualForm.status.trim() && !!manualForm.event_time;

  const quickActions = [
    {
      label: 'Package Received',
      buildPayload: () => ({
        status: 'The package has been received by the shipping agent and is been processed.',
        location: '',
        update_summary: 'Package received by shipping agent',
      }),
    },
    {
      label: 'Loaded Into Container',
      buildPayload: () => ({
        status: 'The package has been loaded into the container and been sent to the port of departure.',
        location: '',
        update_summary: 'Package loaded into container',
      }),
    },
    {
      label: 'Container Loaded On Ship',
      requiresDatePrompt: true,
      datePromptTitle: 'Estimated Arrival Date',
      datePromptLabel: 'Select estimated arrival date',
      buildPayload: (selectedDate) => {
        const etaIso = toApiDateOnly(selectedDate);
        const etaText = formatBracketDateTime(etaIso);
        return {
          status: `The container has been loaded on the ship and is being sent to the port of destination. Estimated arrival date is [${etaText}], then await clearance.`,
          location: '',
          update_summary: 'Container loaded onto ship',
          update_eta: etaIso,
        };
      },
    },
    {
      label: 'Arrived Destination Port',
      requiresDatePrompt: true,
      datePromptTitle: 'Estimated Pickup Date',
      datePromptLabel: 'Select estimated pickup date',
      buildPayload: (selectedDate) => {
        const pickupIso = toApiDateOnly(selectedDate);
        const pickupText = formatBracketDateTime(pickupIso);
        return {
          status: `The package has arrived at the destination port (APAPA) and are waiting for the custom clearance. The estimated pickup time is [${pickupText}]`,
          location: 'APAPA',
          update_summary: 'Arrived destination port, awaiting custom clearance',
          update_pickup_date: selectedDate,
        };
      },
    },
    {
      label: 'Ready to Pickup',
      buildPayload: () => ({
        status: 'The package is ready to pickup.',
        location: 'APAPA',
        update_summary: 'Ready to pickup',
        mark_packages_pickup_available: true,
      }),
    },
  ];

  const postManualEvent = async (payload, successMessage) => {
    setSubmittingManualEvent(true);
    try {
      await createManualShipmentEvent(id, payload);
      setSnackbar({ open: true, message: successMessage, severity: 'success' });
      await loadShipment();
    } catch (err) {
      setSnackbar({ open: true, message: parseApiError(err), severity: 'error' });
    } finally {
      setSubmittingManualEvent(false);
    }
  };

  const submitManualEvent = async () => {
    if (!canSubmitManualEvent) {
      setSnackbar({ open: true, message: 'Status and Event Time are required', severity: 'error' });
      return;
    }

    // Manual form remains available as flexible fallback for custom operational updates.
    const payload = {
      status: manualForm.status.trim(),
      event_time: toApiDateTime(manualForm.event_time),
    };
    const location = manualForm.location.trim();
    const note = manualForm.note.trim();
    const updateSummary = manualForm.update_summary.trim();
    const updateEta = toApiDateOnly(manualForm.update_eta);
    const updatePickupDate = toApiDateOnly(manualForm.update_pickup_date);

    if (location) payload.location = location;
    if (note) payload.note = note;
    if (updateSummary) payload.update_summary = updateSummary;
    if (updateEta) payload.update_eta = updateEta;
    if (updatePickupDate) payload.update_pickup_date = updatePickupDate;

    await postManualEvent(payload, 'Tracking updated successfully');
    setManualForm({
      status: '',
      location: '',
      event_time: getNowDateTimeLocal(),
      note: '',
      update_eta: '',
      update_pickup_date: '',
      update_summary: '',
    });
  };

  const submitQuickAction = async (action) => {
    if (action.requiresDatePrompt) {
      setQuickDateDialog({
        open: true,
        action,
        value: '',
      });
      return;
    }
    const actionPayload = action.buildPayload();
    const payload = {
      status: actionPayload.status,
      location: actionPayload.location,
      event_time: new Date().toISOString(),
      update_summary: actionPayload.update_summary,
    };
    if (actionPayload.update_eta) {
      payload.update_eta = actionPayload.update_eta;
    }
    if (actionPayload.update_pickup_date) {
      payload.update_pickup_date = actionPayload.update_pickup_date;
    }
    if (actionPayload.mark_packages_pickup_available) {
      payload.mark_packages_pickup_available = true;
    }
    await postManualEvent(payload, 'Update added');
  };

  const confirmQuickActionWithDate = async () => {
    if (!quickDateDialog.action) return;
    if (!quickDateDialog.value) {
      setSnackbar({ open: true, message: 'Please select a date', severity: 'error' });
      return;
    }
    const actionPayload = quickDateDialog.action.buildPayload(quickDateDialog.value);
    const payload = {
      status: actionPayload.status,
      location: actionPayload.location,
      event_time: new Date().toISOString(),
      update_summary: actionPayload.update_summary,
    };
    if (actionPayload.update_eta) {
      payload.update_eta = actionPayload.update_eta;
    }
    if (actionPayload.update_pickup_date) {
      payload.update_pickup_date = actionPayload.update_pickup_date;
    }
    if (actionPayload.mark_packages_pickup_available) {
      payload.mark_packages_pickup_available = true;
    }
    setQuickDateDialog({ open: false, action: null, value: '' });
    await postManualEvent(payload, 'Update added');
  };

  const submitEtaOnlyUpdate = async () => {
    const etaIso = toApiDateOnly(manualForm.update_eta);
    if (!etaIso) {
      setSnackbar({ open: true, message: 'Please select ETA date', severity: 'error' });
      return;
    }
    await postManualEvent({ update_eta: etaIso }, 'ETA updated successfully');
  };

  const submitPickupDateOnlyUpdate = async () => {
    const pickupDate = toApiDateOnly(manualForm.update_pickup_date);
    if (!pickupDate) {
      setSnackbar({ open: true, message: 'Please select pickup date', severity: 'error' });
      return;
    }
    await postManualEvent({ update_pickup_date: pickupDate }, 'Pickup date updated successfully');
  };

  const confirmInternationalFees = async () => {
    const proceed = window.confirm(
      'Mark all package international shipping fees in this shipment as final/payable? This will not send any customer notification.'
    );
    if (!proceed) return;

    setConfirmingFees(true);
    try {
      const result = await confirmShipmentInternationalFees(id, { notify: false });
      setShipment(result.shipment);
      setSnackbar({
        open: true,
        message: `Confirmed ${result.confirmed_count || 0} package fee(s)`,
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({ open: true, message: parseApiError(err), severity: 'error' });
    } finally {
      setConfirmingFees(false);
    }
  };

  const handleMarkShipmentSent = async () => {
    const proceed = window.confirm(
      'Mark this shipment as sent? After this, packages can no longer be added or removed from this shipment.'
    );
    if (!proceed) return;

    setMarkingSent(true);
    try {
      const data = await markShipmentSent(id);
      setShipment(data);
      setSnackbar({ open: true, message: 'Shipment marked as sent', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: parseApiError(err), severity: 'error' });
    } finally {
      setMarkingSent(false);
    }
  };

  if (!shipment) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5">Shipment Detail</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => navigate('/admin/shipments')}>Back</Button>
          <Button variant="contained" onClick={() => navigate(`/admin/shipments/${id}/edit`)}>Edit</Button>
        </Stack>
      </Stack>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6">{shipment.shipment_number || '-'}</Typography>
          <Typography sx={{ mt: 1 }}>Container Number: {shipment.container_number || '-'}</Typography>
          <Chip size="small" label={shipment.status} sx={{ mt: 1 }} />
          <Typography sx={{ mt: 1 }}>Carrier: {shipment.carrier || '-'}</Typography>
          <Typography>Provider: {shipment.provider || '-'}</Typography>
          <Typography>Shipment Type: {shipment.shipment_type || '-'}</Typography>
          <Typography>Vessel: {shipment.vessel_name || '-'}</Typography>
          <Typography>Voyage: {shipment.voyage_number || '-'}</Typography>
          <Typography>Origin: {shipment.origin_port || '-'}</Typography>
          <Typography>Destination: {shipment.destination_port || '-'}</Typography>
          <Typography>ETD (System): {shipment.etd || '-'}</Typography>
          <Typography>ETA (System): {shipment.eta || '-'}</Typography>
          <Typography>ETA (Manual): {shipment.manual_eta || '-'}</Typography>
          <Typography>Pickup Date (System): {shipment.pickup_date || '-'}</Typography>
          <Typography>Pickup Date (Manual): {shipment.manual_pickup_date || '-'}</Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
            <Typography>Sent Status:</Typography>
            <Chip
              size="small"
              color={shipment.is_sent ? 'success' : 'warning'}
              label={shipment.is_sent ? 'Sent' : 'Not Sent'}
            />
          </Stack>
          {shipment.sent_at && (
            <Typography>Sent At: {new Date(shipment.sent_at).toLocaleString()}</Typography>
          )}
          <Typography>
            International Fees: {shipment.international_shipping_fee_confirmed ? 'Final/Payable' : 'Estimate'}
          </Typography>
          {shipment.international_shipping_fee_confirmed_at && (
            <Typography>
              Fee Confirmed At: {new Date(shipment.international_shipping_fee_confirmed_at).toLocaleString()}
            </Typography>
          )}
          <Typography>Last Synced: {shipment.last_synced ? new Date(shipment.last_synced).toLocaleString() : '-'}</Typography>
          <Typography>Created At: {shipment.created_at ? new Date(shipment.created_at).toLocaleString() : '-'}</Typography>
          <Typography>Updated At: {shipment.updated_at ? new Date(shipment.updated_at).toLocaleString() : '-'}</Typography>
          <Button
            variant="contained"
            color={shipment.international_shipping_fee_confirmed ? 'warning' : 'success'}
            sx={{ mt: 2 }}
            onClick={confirmInternationalFees}
            disabled={confirmingFees}
          >
            {confirmingFees
              ? 'Confirming...'
              : (shipment.international_shipping_fee_confirmed ? 'Reconfirm International Fees' : 'Confirm International Fees')}
          </Button>
          {!shipment.is_sent && (
            <Button
              variant="contained"
              color="primary"
              sx={{ mt: 2, ml: 1 }}
              onClick={handleMarkShipmentSent}
              disabled={markingSent}
            >
              {markingSent ? 'Marking...' : 'Mark Shipment as Sent'}
            </Button>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6">Package Numbers</Typography>
          <List>
            {(shipment.items || []).map((item) => (
              <ListItem key={item.id} divider>
                <ListItemText
                  primary={item.package_number}
                  secondary={(
                    <Stack spacing={0.5}>
                      <Typography variant="body2" color="text.secondary">
                        Added: {item.created_at ? new Date(item.created_at).toLocaleString() : '-'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Method: {item.shipping_method || '-'} | Volume: {item.package_cbm || '-'} cbm | Weight: {item.package_weight || '-'} kg | Volume Weight: {item.volume_weight || '-'} kg
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        International Fee: {item.international_shipping_cost ? `NGN ${Number(item.international_shipping_cost).toLocaleString()}` : '-'}
                      </Typography>
                    </Stack>
                  )}
                />
                <Chip
                  size="small"
                  color={item.international_shipping_fee_paid ? 'success' : (item.international_shipping_fee_status === 'confirmed' ? 'primary' : 'warning')}
                  label={item.international_shipping_fee_paid ? 'Paid' : (item.international_shipping_fee_status === 'confirmed' ? 'Final' : 'Estimate')}
                />
              </ListItem>
            ))}
          </List>
          <Divider sx={{ my: 1 }} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Total Volume Weight: {totalVolumeWeight.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kg
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Total CBM: {totalCbm.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })} cbm
          </Typography>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6">Tracking Timeline</Typography>
          <Divider sx={{ my: 1 }} />
          <List>
            {timeline.map((event) => (
              <ListItem key={event.id} divider>
                <ListItemText
                  primary={(
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2">
                        {event.status}{event.location ? ` - ${event.location}` : ''}
                      </Typography>
                      {event.source === 'manual' && <Chip label="Manual" color="warning" size="small" />}
                    </Stack>
                  )}
                  secondary={new Date(event.event_time).toLocaleString()}
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>Quick Tracking Actions</Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1, mb: 2 }}>
            {quickActions.map((action) => (
              <Button
                key={action.label}
                size="small"
                variant="outlined"
                disabled={submittingManualEvent}
                onClick={() => submitQuickAction(action)}
              >
                {action.label}
              </Button>
            ))}
          </Stack>

          <Divider sx={{ mb: 2 }} />

          <Typography variant="h6">Manual Tracking Update</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add operational fallback timeline updates when carrier automation is unavailable.
          </Typography>

          <Stack spacing={2}>
            <TextField
              label="Status"
              placeholder="e.g. Loaded on vessel"
              value={manualForm.status}
              onChange={(e) => setManualForm((prev) => ({ ...prev, status: e.target.value }))}
              required
            />
            <TextField
              label="Location"
              placeholder="e.g. Shanghai Port"
              value={manualForm.location}
              onChange={(e) => setManualForm((prev) => ({ ...prev, location: e.target.value }))}
            />
            <TextField
              label="Event Time"
              type="datetime-local"
              value={manualForm.event_time}
              onChange={(e) => setManualForm((prev) => ({ ...prev, event_time: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              required
            />
            <TextField
              label="Note"
              placeholder="Any additional note"
              value={manualForm.note}
              onChange={(e) => setManualForm((prev) => ({ ...prev, note: e.target.value }))}
              multiline
              minRows={3}
            />
            <TextField
              label="Update ETA"
              type="date"
              value={manualForm.update_eta}
              onChange={(e) => setManualForm((prev) => ({ ...prev, update_eta: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Update Pickup Date"
              type="date"
              value={manualForm.update_pickup_date}
              onChange={(e) => setManualForm((prev) => ({ ...prev, update_pickup_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Update Summary"
              placeholder="Short summary shown to user"
              value={manualForm.update_summary}
              onChange={(e) => setManualForm((prev) => ({ ...prev, update_summary: e.target.value }))}
            />
            <Box>
              <Button
                variant="contained"
                onClick={submitManualEvent}
                disabled={submittingManualEvent || !canSubmitManualEvent}
              >
                {submittingManualEvent ? 'Saving...' : 'Add Manual Event'}
              </Button>
              <Button
                variant="outlined"
                sx={{ ml: 1 }}
                onClick={submitEtaOnlyUpdate}
                disabled={submittingManualEvent || !manualForm.update_eta}
              >
                {submittingManualEvent ? 'Saving...' : 'Update ETA Only'}
              </Button>
              <Button
                variant="outlined"
                sx={{ ml: 1 }}
                onClick={submitPickupDateOnlyUpdate}
                disabled={submittingManualEvent || !manualForm.update_pickup_date}
              >
                {submittingManualEvent ? 'Saving...' : 'Update Pickup Date Only'}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Dialog
        open={quickDateDialog.open}
        onClose={() => setQuickDateDialog({ open: false, action: null, value: '' })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{quickDateDialog.action?.datePromptTitle || 'Select Date'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            type="date"
            fullWidth
            label={quickDateDialog.action?.datePromptLabel || 'Date'}
            value={quickDateDialog.value}
            onChange={(e) => setQuickDateDialog((prev) => ({ ...prev, value: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuickDateDialog({ open: false, action: null, value: '' })}>Cancel</Button>
          <Button variant="contained" onClick={confirmQuickActionWithDate}>Confirm</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
